import { Box } from '@mui/material';

interface VarReviewBannerProps {
  /**
   * Compact variant for rows built out of 20px pills (the soccer `ScoreLine`),
   * where the full-size banner visually dominates and misaligns the row.
   */
  dense?: boolean;
}

/**
 * A prominent, high-contrast VAR banner. Purely a RENDER of the backend-authored
 * `MatchState.lastVarActive` flag (VAR incident Id 1074) — the FE derives nothing
 * from the raw incident stream. Callers render it conditionally on
 * `state?.lastVarActive`; it draws itself unmissable (warning background, black
 * text, a subtle pulse) so an operator instantly sees a review is in progress.
 *
 * Shared by SniperEventCard and LiveMatchEventsTab's soccer ScoreLine so the two
 * surfaces stay identical.
 *
 * The pulse animates `opacity` only — deliberately NOT `transform: scale`, which
 * would grow the banner past the 8px gap of its host row and collide with the
 * adjacent chips at the top of every cycle.
 */
export function VarReviewBanner({ dense = false }: VarReviewBannerProps) {
  return (
    <Box
      role="status"
      // `polite`, not `assertive`: the pulse already carries the urgency, VAR is
      // not an error condition, and several matches can be under review at once —
      // assertive regions interrupt each other and the operator would lose all but
      // one announcement (they are not distinguishable by text alone).
      aria-live="polite"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: dense ? 0.5 : 0.75,
        px: dense ? 0.75 : 1.5,
        py: dense ? 0 : 0.5,
        ...(dense ? { height: 20 } : null),
        borderRadius: 1,
        bgcolor: 'warning.main',
        color: 'warning.contrastText',
        fontWeight: dense ? 700 : 800,
        fontSize: dense ? '0.7rem' : '0.8rem',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
        boxShadow: dense ? 0 : 3,
        border: dense ? '1px solid' : '2px solid',
        borderColor: 'warning.dark',
        animation: 'var-review-pulse 1.2s ease-in-out infinite',
        '@keyframes var-review-pulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.6 },
        },
        '@media (prefers-reduced-motion: reduce)': {
          animation: 'none',
        },
      }}
    >
      <span aria-hidden="true">📺 🚨</span>
      VAR — REVIEW IN PROGRESS
    </Box>
  );
}
